import { useEffect, useId, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Filter } from "lucide-react"

/** Active Today-button purple from Ads Manager date presets. */
const ACTIVE_FILTER_COLOR = "#7488e8"

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "ENABLE", label: "Active" },
  { value: "DISABLE", label: "Paused" },
  { value: "UNKNOWN", label: "Unknown" },
] as const

interface ColumnStatusFilterProps {
  value: string
  onChange: (value: string) => void
  showUnknownStatus?: boolean
}

export function ColumnStatusFilter({
  value,
  onChange,
  showUnknownStatus = false,
}: ColumnStatusFilterProps) {
  const active = value !== "all"
  const [open, setOpen] = useState(false)
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(
    null,
  )
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()

  const options = STATUS_OPTIONS.filter(
    (opt) => opt.value !== "UNKNOWN" || showUnknownStatus,
  )

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) {
      setPanelPos(null)
      return
    }
    const place = () => {
      const rect = buttonRef.current!.getBoundingClientRect()
      const width = 180
      const left = Math.min(
        Math.max(8, rect.right - width),
        window.innerWidth - width - 8,
      )
      setPanelPos({ top: rect.bottom + 6, left })
    }
    place()
    window.addEventListener("scroll", place, true)
    window.addEventListener("resize", place)
    return () => {
      window.removeEventListener("scroll", place, true)
      window.removeEventListener("resize", place)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node
      if (
        buttonRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return
      }
      setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("pointerdown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={active ? "Edit status filter" : "Filter by status"}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        onMouseDown={(e) => e.stopPropagation()}
        className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm text-white/45 hover:text-white/80"
        style={active ? { color: ACTIVE_FILTER_COLOR } : undefined}
      >
        <Filter className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden />
      </button>

      {open &&
        panelPos &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-labelledby={titleId}
            className="fixed z-[80] w-[180px] rounded-md border border-[#555] bg-[#2d2d2d] p-2 shadow-lg"
            style={{ top: panelPos.top, left: panelPos.left }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div
              id={titleId}
              className="mb-1.5 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-white/55"
            >
              Status
            </div>
            <div className="flex flex-col gap-0.5">
              {options.map((opt) => {
                const selected = value === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onChange(opt.value)
                      setOpen(false)
                    }}
                    className={`rounded px-2 py-1.5 text-left text-xs ${
                      selected
                        ? "bg-[#7488e8]/25 font-medium text-[#c5cef7]"
                        : "text-white hover:bg-[#3a3a3a]"
                    }`}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}

export function statusFilterChipLabel(status: string): string | null {
  if (status === "all") return null
  const opt = STATUS_OPTIONS.find((o) => o.value === status)
  return opt ? `Status: ${opt.label}` : `Status: ${status}`
}
